import React, { useState } from 'react';
import { X, Car, User, Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ORDER_SERVICE_TYPES } from '../../../utils/orderConstants';

interface OrderSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: OrderSetupData) => void;
  isCreating?: boolean;
  initialData?: Partial<OrderSetupData>;
  isEditMode?: boolean;
}

export interface OrderSetupData {
  name: string;
  vin: string;
  buyer?: string;
  service_type?: string;
  language?: string;
}

const OrderSetupModal: React.FC<OrderSetupModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  isCreating = false,
  initialData,
  isEditMode = false
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(initialData?.name || '');
  const [vin, setVin] = useState(initialData?.vin || '');
  const [buyer, setBuyer] = useState(initialData?.buyer || '');
  const [serviceType, setServiceType] = useState(initialData?.service_type || '');
  const [vinError, setVinError] = useState('');
  const formRef = React.useRef<HTMLFormElement>(null);

  // VIN validation regex
  const VIN_REGEX = /^\b[(A-H|J-N|P|R-Z|0-9)]{17}\b/;

  // Update form fields when initialData changes (for edit mode)
  React.useEffect(() => {
    if (isEditMode && initialData) {
      setName(initialData.name || '');
      setVin(initialData.vin || '');
      setBuyer(initialData.buyer || '');
      setServiceType(initialData.service_type || '');
    }
  }, [isEditMode, initialData]);

  // Validate VIN on change
  const handleVinChange = (value: string) => {
    const upperValue = value.toUpperCase();
    setVin(upperValue);
    
    if (upperValue.length === 0) {
      setVinError('');
    } else if (upperValue.length < 17) {
      setVinError('');
    } else if (upperValue.length === 17) {
      if (!VIN_REGEX.test(upperValue)) {
        setVinError(t('orders.setupModal.invalidVin', { defaultValue: 'Invalid VIN format' }));
      } else {
        setVinError('');
      }
    }
  };

  const serviceTypes = ORDER_SERVICE_TYPES.map((st) => ({
    value: st.value,
    label: t(st.labelKey, { defaultValue: st.defaultLabel }),
  }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedVin = vin.trim();
    
    if (!trimmedVin) {
      return;
    }

    // Validate VIN format
    if (!VIN_REGEX.test(trimmedVin)) {
      setVinError(t('orders.setupModal.invalidVin', { defaultValue: 'Invalid VIN format' }));
      return;
    }

    onSubmit({
      name: name.trim() || trimmedVin,
      vin: trimmedVin,
      buyer: buyer.trim() || undefined,
      service_type: serviceType || undefined,
    });

    // Reset form
    setName('');
    setVin('');
    setBuyer('');
    setServiceType('');
    setVinError('');
  };

  const handleClose = () => {
    // Reset form on close
    setName('');
    setVin('');
    setBuyer('');
    setServiceType('');
    setVinError('');
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isCreating && vin.trim() && !vinError) {
      e.preventDefault();
      formRef.current?.requestSubmit();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div 
        className="bg-white rounded-3xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden"
        onKeyDown={handleKeyDown}
      >
        {/* Header - Minimalist */}
        <div className="relative px-8 pt-8 pb-6 border-b border-gray-100">
          <button
            onClick={handleClose}
            className="absolute right-6 top-6 p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
            disabled={isCreating}
          >
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-semibold text-gray-900">
            {isEditMode 
              ? t('orders.setupModal.editTitle', { defaultValue: 'Edit Order' })
              : t('orders.setupModal.title', { defaultValue: 'Create New Order' })
            }
          </h2>
        </div>

        {/* Form */}
        <form ref={formRef} onSubmit={handleSubmit} className="px-8 py-6 space-y-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Order Name - Required */}

          {/* VIN - Required */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
              <Car className="w-4 h-4 text-gray-500" />
              {t('orders.setupModal.vin', { defaultValue: 'Vehicle VIN' })}
            </label>
            <input
              type="text"
              value={vin}
              onChange={(e) => handleVinChange(e.target.value)}
              placeholder={t('orders.setupModal.vinPlaceholder', { defaultValue: '17-character VIN' })}
              className={`w-full px-4 py-3 bg-gray-50 border-0 rounded-xl focus:outline-none focus:ring-2 ${
                vinError ? 'focus:ring-red-500' : 'focus:ring-gray-900'
              } focus:bg-white transition-all font-mono tracking-wide placeholder:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed ${
                vinError ? 'ring-2 ring-red-500' : ''
              }`}
              maxLength={17}
              disabled={isCreating || isEditMode}
              required
              autoFocus={!isEditMode}
            />
            {vinError ? (
              <p className="text-xs text-red-600 px-1">
                {vinError}
              </p>
            ) : (
              <p className="text-xs text-gray-500 px-1">
                {isEditMode 
                  ? t('orders.setupModal.vinNotEditable', { defaultValue: 'VIN cannot be changed after order creation' })
                  : t('orders.setupModal.vinHelp', { defaultValue: 'Enter the 17-character vehicle identification number' })
                }
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
              {t('orders.setupModal.orderName', { defaultValue: 'Order Name' })}
              <span className="text-xs font-normal text-gray-400">
                ({t('orders.setupModal.optional', { defaultValue: 'optional' })})
              </span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('orders.setupModal.orderNamePlaceholder', { defaultValue: 'e.g., Customer A - Front Bumper' })}
              className="w-full px-4 py-3 bg-gray-50 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:bg-white transition-all placeholder:text-gray-400"
              disabled={isCreating}
            />
            <p className="text-xs text-gray-500 px-1">
              {t('orders.setupModal.orderNameHelp', { defaultValue: 'Leave empty to use VIN as order name' })}
            </p>
          </div>

          {/* Buyer - Optional */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
              <User className="w-4 h-4 text-gray-500" />
              {t('orders.setupModal.buyer', { defaultValue: 'Buyer Name' })}
              <span className="text-xs font-normal text-gray-400">
                ({t('orders.setupModal.optional', { defaultValue: 'optional' })})
              </span>
            </label>
            <input
              type="text"
              value={buyer}
              onChange={(e) => setBuyer(e.target.value)}
              placeholder={t('orders.setupModal.buyerPlaceholder', { defaultValue: 'Customer or company name' })}
              className="w-full px-4 py-3 bg-gray-50 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:bg-white transition-all placeholder:text-gray-400"
              disabled={isCreating}
            />
          </div>

          {/* Service Type - Optional */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
              <Wrench className="w-4 h-4 text-gray-500" />
              {t('orders.setupModal.serviceType', { defaultValue: 'Service Type' })}
              <span className="text-xs font-normal text-gray-400">
                ({t('orders.setupModal.optional', { defaultValue: 'optional' })})
              </span>
            </label>
            <select
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:bg-white transition-all text-gray-700 appearance-none cursor-pointer"
              disabled={isCreating}
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1.25rem',
                paddingRight: '2.5rem'
              }}
            >
              <option value="" className="text-gray-400">
                {t('orders.setupModal.selectServiceType', { defaultValue: 'Select a service type...' })}
              </option>
              {serviceTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </form>

        {/* Footer Buttons */}
        <div className="px-8 py-6 bg-gray-50 border-t border-gray-100 flex gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 px-5 py-3 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
            disabled={isCreating}
          >
            {t('orders.setupModal.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            type="submit"
            onClick={(e) => {
              e.preventDefault();
              formRef.current?.requestSubmit();
            }}
            className="flex-1 px-5 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all font-medium disabled:opacity-40 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
            disabled={!vin.trim() || isCreating || !!vinError}
          >
            {isCreating 
              ? (isEditMode 
                  ? t('orders.setupModal.updating', { defaultValue: 'Updating...' })
                  : t('orders.setupModal.creating', { defaultValue: 'Creating...' })
                )
              : (isEditMode
                  ? t('orders.setupModal.update', { defaultValue: 'Update Order' })
                  : t('orders.setupModal.create', { defaultValue: 'Create Order' })
                )
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderSetupModal;
